import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PromoRedeem } from "@/components/PromoRedeem";
import { PromoStatus } from "@/components/PromoStatus";
import { DevicesCard } from "@/components/DevicesCard";
import { BillingActions } from "@/components/BillingActions";
import { startCheckout, errorMessage } from "@/lib/api";
import { longDate } from "@/lib/time";

function statusText(user) {
  if (user.promo_access) return "Free access (promo code)";
  if (user.role === "admin") return "Full access";
  if (user.payment_locked) return "Payment paused — update your card";
  if (user.payment_issue) return "Active · payment retrying";
  if (user.trialing) return user.cancel_at_period_end ? `Free trial · ends ${longDate(user.trial_ends_at)} (cancelled)` : `Free trial · first charge ${longDate(user.trial_ends_at)}`;
  if (user.subscription_status === "active") return "Active subscription";
  if (user.needs_card) return "Add a card to start your free trial";
  return user.trial_active ? `Free trial · ${user.trial_days_left} ${user.trial_days_left === 1 ? "day" : "days"} left` : "Trial ended";
}

function SubscribeButton({ user, highlight }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const price = user.role === "vendor" ? "$25/mo" : user.family_plan ? "$19.99/mo" : "$9.99/mo";
  const trialCard = user.needs_card || user.trial_active;
  const go = async () => {
    if (!trialCard) return navigate("/pricing");
    setBusy(true);
    try { await startCheckout(user.checkout_plan); } catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  const days = user.family_plan ? 10 : 7;
  const label = user.needs_card ? `Add card — start your ${days}-day free trial`
    : trialCard ? `Add card — free until your trial ends, then ${price}`
    : user.role === "vendor" ? "Activate vendor plan — $25/mo" : user.family_plan ? "Subscribe to Family — $19.99/mo" : "Subscribe — $9.99/mo";
  return (
    <Button data-testid="account-subscribe" disabled={busy} onClick={go}
      className={`w-full h-12 rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold ${highlight ? "ring-4 ring-[#FF3B6B]/40" : ""}`}>
      {busy ? "Opening secure checkout…" : label}
    </Button>
  );
}

export default function Account() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const active = user.subscription_status === "active";
  const good = !user.payment_issue && (active || (user.trial_active && !user.needs_card));

  return (
    <main className="max-w-2xl mx-auto px-4 py-16">
      <h1 className="text-4xl font-extrabold tracking-tight">Hi, {user.name}</h1>
      <div className="mt-8 rounded-3xl bg-[#151C2C] border border-[#26334D] p-8 space-y-4">
        <div className="flex justify-between text-sm"><span className="text-slate-400">Email</span><span data-testid="account-email">{user.email}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-400">Account type</span><span data-testid="account-role" className="capitalize">{user.role === "user" ? (user.is_family_member ? "Family member (own login)" : user.family_plan ? "Family traveler" : "Traveler") : user.role}</span></div>
        <div className="flex justify-between gap-4 text-sm"><span className="text-slate-400">Status</span><span data-testid="account-status" className={`text-right ${good ? "text-[#00E5A3]" : "text-[#FF7A7A]"}`}>{statusText(user)}</span></div>
        {user.trialing && (
          <p data-testid="account-trial-note" className="text-xs text-slate-400">
            Your card will be charged automatically on {longDate(user.trial_ends_at)}. Cancel anytime before then with Manage billing.
          </p>
        )}
        {!user.promo_access && <PromoRedeem />}
        {!active && user.role !== "admin" && !user.is_family_member && <SubscribeButton user={user} highlight={params.get("add_card") === "1"} />}
        <BillingActions />
      </div>
      <DevicesCard />
      {user.role === "admin" && <PromoStatus />}
    </main>
  );
}
